import { random } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GAME_CHARACTERS, GAME_CONFIG } from '../../../../constants/gameCharacters';
import { GameStartScreen } from '../common/GameStartScreen';
import { MoleGameBoard } from './MoleGameBoard';
import { MoleGameStats } from './MoleGameStats';
import { gameWrapperStyle } from './styles';
import type { ActiveCharacter, MoleGameProps, ScorePopup } from './types';

export const MoleGame = ({ isGameStarted, onGameStart, onGameOver }: MoleGameProps) => {
  const [timeLeft, setTimeLeft] = useState<number>(GAME_CONFIG.TOTAL_TIME);
  const [score, setScore] = useState(0);
  const [activeCharacters, setActiveCharacters] = useState<ActiveCharacter[]>([]);
  const [scorePopups, setScorePopups] = useState<ScorePopup[]>([]);
  const [clickedSharkIds, setClickedSharkIds] = useState<Set<string>>(new Set());

  const spawnIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when game starts
  useEffect(() => {
    if (isGameStarted) {
      setTimeLeft(GAME_CONFIG.TOTAL_TIME);
      setScore(0);
      setActiveCharacters([]);
      setClickedSharkIds(new Set());
      setScorePopups([]);
    }
  }, [isGameStarted]);

  // Timer logic
  useEffect(() => {
    if (!isGameStarted) {
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isGameStarted]);

  // Game over when time runs out
  useEffect(() => {
    if (timeLeft === 0 && isGameStarted) {
      onGameOver(score);
    }
  }, [timeLeft, score, onGameOver, isGameStarted]);

  // Character spawn logic
  useEffect(() => {
    if (!isGameStarted) {
      return;
    }

    const spawnCharacter = () => {
      setActiveCharacters((prev) => {
        if (prev.length >= GAME_CONFIG.MAX_ACTIVE_CHARACTERS) {
          return prev;
        }

        const spawnCount = random(1, 3, false);
        const newCharacters: ActiveCharacter[] = [];
        const occupiedPositions = new Set(prev.map((c) => c.position));

        for (let i = 0; i < spawnCount; i++) {
          if (prev.length + newCharacters.length >= GAME_CONFIG.MAX_ACTIVE_CHARACTERS) {
            break;
          }

          let position: number;
          let attempts = 0;
          do {
            position = random(0, GAME_CONFIG.GRID_SIZE - 1, false);
            attempts++;
            if (attempts > 20) {
              break;
            }
          } while (occupiedPositions.has(position) && attempts < 20);

          if (attempts >= 20) {
            continue;
          }
          occupiedPositions.add(position);

          const character = GAME_CHARACTERS[random(0, GAME_CHARACTERS.length - 1, false)];

          newCharacters.push({
            id: `${character.id}-${Date.now()}-${i}`,
            character,
            position,
            spawnedAt: Date.now(),
          });
        }

        return [...prev, ...newCharacters];
      });
    };

    const scheduleNextSpawn = () => {
      const interval = random(GAME_CONFIG.SPAWN_INTERVAL_MIN, GAME_CONFIG.SPAWN_INTERVAL_MAX, true);

      spawnIntervalRef.current = setTimeout(() => {
        spawnCharacter();
        scheduleNextSpawn();
      }, interval);
    };

    scheduleNextSpawn();

    return () => {
      if (spawnIntervalRef.current) {
        clearTimeout(spawnIntervalRef.current);
      }
    };
  }, [isGameStarted]);

  // Character auto-removal logic
  useEffect(() => {
    if (!isGameStarted) {
      return;
    }

    const checkInterval = setInterval(() => {
      const now = Date.now();
      setActiveCharacters((prev) =>
        prev.filter((char) => now - char.spawnedAt < char.character.duration),
      );
    }, 50);

    return () => clearInterval(checkInterval);
  }, [isGameStarted]);

  // Character click handler
  const handleCharacterClick = useCallback(
    (characterId: string, characterScore: number, position: number, isShark: boolean) => {
      if (isShark) {
        setClickedSharkIds((prev) => new Set(prev).add(characterId));

        setTimeout(() => {
          setActiveCharacters((prev) => prev.filter((char) => char.id !== characterId));
          setClickedSharkIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(characterId);
            return newSet;
          });
        }, 1200);
      } else {
        setTimeout(() => {
          setActiveCharacters((prev) => prev.filter((char) => char.id !== characterId));
        }, 600);
      }

      setScore((prev) => prev + characterScore);

      const popupId = `popup-${Date.now()}-${random()}`;
      setScorePopups((prev) => [
        ...prev,
        { id: popupId, score: characterScore, position, isShark },
      ]);

      setTimeout(() => {
        setScorePopups((prev) => prev.filter((p) => p.id !== popupId));
      }, 1000);
    },
    [],
  );

  if (!isGameStarted) {
    return (
      <GameStartScreen onGameStart={onGameStart}>
        <img
          src={`${process.env.NEXT_PUBLIC_CDN_URL}/public/games/s_guide.png`}
          alt="mole-game-guide"
        />
      </GameStartScreen>
    );
  }

  return (
    <div css={gameWrapperStyle}>
      <MoleGameStats timeLeft={timeLeft} score={score} />
      <MoleGameBoard
        activeCharacters={activeCharacters}
        clickedSharkIds={clickedSharkIds}
        scorePopups={scorePopups}
        onCharacterClick={handleCharacterClick}
      />
    </div>
  );
};
